import { createContext, useContext } from 'react';

const AuthCtx = createContext<any>({ user: { name: 'You', email: '[email protected]', tier: 'developer' } });
const AuthProvider = ({ children }: any) => <AuthCtx.Provider value={{ user: { name: 'You', email: '[email protected]', tier: 'developer' } }}>{children}</AuthCtx.Provider>;
const useAuth = () => useContext(AuthCtx);
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './styles/global.css';

const MCP_GATEWAY = (import.meta.env.VITE_MCP_GATEWAY_URL as string) || 'http://localhost:3008';
const MODEL_GATEWAY = (import.meta.env.VITE_MODEL_GATEWAY_URL as string) || 'http://localhost:8092';

type Role = 'system' | 'user' | 'assistant';
type Message = {
  id: string;
  role: Role;
  content: string;
  model?: string;
  timestamp: Date;
  toolCalls?: { tool: string; params: Record<string, string>; result: string; success: boolean }[];
};

type Provider = { id: string; name: string; baseUrl: string; enabled: boolean };

type Model = {
  id: string;
  name: string;
  provider: string;
  description: string;
  icon: string;
  iconBg: string;
  tag: 'code' | 'reason' | 'vision' | 'voice' | 'secure' | 'fast' | 'general';
  context: string;
  tier: 'free' | 'developer' | 'team' | 'enterprise';
};

type MCPTool = {
  id: string;
  name: string;
  category: string;
  description: string;
  params: Record<string, string>;
  icon: string;
};

type Conversation = { id: string; name: string; messages: Message[]; createdAt: Date };

// === PROVIDERS ===
const PROVIDERS: Provider[] = [
  { id: 'aether', name: 'Aether', baseUrl: MODEL_GATEWAY, enabled: true },
  { id: 'openrouter', name: 'OpenRouter', baseUrl: 'https://openrouter.ai/api/v1', enabled: true },
  { id: 'ollama', name: 'Ollama', baseUrl: 'http://localhost:11434/v1', enabled: true },
  { id: 'openai', name: 'OpenAI', baseUrl: 'https://api.openai.com/v1', enabled: true },
  { id: 'anthropic', name: 'Anthropic', baseUrl: 'https://api.anthropic.com/v1', enabled: true },
  { id: 'huggingface', name: 'HuggingFace', baseUrl: 'https://api-inference.huggingface.co/models', enabled: true },
];

// === MODELS — across providers ===
const MODELS: Model[] = [
  { id: 'aether-orion', name: 'Orion', provider: 'aether', description: 'Code & creative generation', icon: '✦', iconBg: 'linear-gradient(135deg, #d6a94e, #ff6a1a)', tag: 'code', context: '128K', tier: 'developer' },
  { id: 'aether-vega', name: 'Vega', provider: 'aether', description: 'Deep reasoning & analysis', icon: '◆', iconBg: 'linear-gradient(135deg, #8b5cf6, #00e5ff)', tag: 'reason', context: '128K', tier: 'developer' },
  { id: 'aether-atlas', name: 'Atlas', provider: 'aether', description: 'Coding agent, planning & tools', icon: '◆', iconBg: 'linear-gradient(135deg, #f59e0b, #d6a94e)', tag: 'code', context: '128K', tier: 'developer' },
  { id: 'aether-nova', name: 'Nova', provider: 'aether', description: 'Vision & multimodal', icon: '◈', iconBg: 'linear-gradient(135deg, #10a37f, #00e5ff)', tag: 'vision', context: '128K', tier: 'developer' },
  { id: 'aether-lyra', name: 'Lyra', provider: 'aether', description: 'Voice & speech synthesis', icon: '♢', iconBg: 'linear-gradient(135deg, #ec4899, #8b5cf6)', tag: 'voice', context: '—', tier: 'developer' },
  { id: 'aether-draco', name: 'Draco', provider: 'aether', description: 'Security analysis', icon: '⬢', iconBg: 'linear-gradient(135deg, #ef4444, #d6a94e)', tag: 'secure', context: '128K', tier: 'team' },
  { id: 'aether-mini', name: 'Mini', provider: 'aether', description: 'Lightweight fast inference', icon: '◇', iconBg: 'linear-gradient(135deg, #10a37f, #047857)', tag: 'fast', context: '128K', tier: 'free' },
  { id: 'aether-tiny', name: 'Tiny', provider: 'aether', description: 'Ultra-lightweight', icon: '○', iconBg: 'linear-gradient(135deg, #64748b, #475569)', tag: 'fast', context: '128K', tier: 'free' },
  { id: 'aether-hunter', name: 'Hunter', provider: 'aether', description: 'Agentic code assistant', icon: '⬡', iconBg: 'linear-gradient(135deg, #f59e0b, #f97316)', tag: 'code', context: '128K', tier: 'team' },
  { id: 'aether-healer', name: 'Healer', provider: 'aether', description: 'Code fix & debug specialist', icon: '✚', iconBg: 'linear-gradient(135deg, #10b981, #059669)', tag: 'code', context: '128K', tier: 'team' },
  { id: 'openai-gpt-4o', name: 'GPT-4o', provider: 'openai', description: 'OpenAI flagship multimodal', icon: 'G', iconBg: 'linear-gradient(135deg, #10a37f, #0d8a6a)', tag: 'general', context: '128K', tier: 'team' },
  { id: 'openai-o1', name: 'o1', provider: 'openai', description: 'OpenAI reasoning model', icon: 'o', iconBg: 'linear-gradient(135deg, #1a1a1a, #424242)', tag: 'reason', context: '200K', tier: 'enterprise' },
  { id: 'claude-sonnet', name: 'Claude Sonnet 4', provider: 'anthropic', description: 'Anthropic balanced reasoning', icon: 'A', iconBg: 'linear-gradient(135deg, #d97706, #b45309)', tag: 'reason', context: '200K', tier: 'team' },
  { id: 'claude-opus', name: 'Claude Opus 4', provider: 'anthropic', description: 'Anthropic most capable', icon: 'A', iconBg: 'linear-gradient(135deg, #7c2d12, #431407)', tag: 'reason', context: '200K', tier: 'enterprise' },
  { id: 'llama-3.3', name: 'Llama 3.3 70B', provider: 'ollama', description: 'Local Meta model', icon: 'L', iconBg: 'linear-gradient(135deg, #2563eb, #1d4ed8)', tag: 'general', context: '128K', tier: 'free' },
  { id: 'mistral', name: 'Mistral Large', provider: 'openrouter', description: 'Mistral via OpenRouter', icon: 'M', iconBg: 'linear-gradient(135deg, #f97316, #ea580c)', tag: 'general', context: '128K', tier: 'developer' },
];

const MCP_TOOLS: MCPTool[] = [
  { id: 'read_file', name: 'Read File', category: 'Filesystem', description: 'Read file contents', params: { path: '' }, icon: '📄' },
  { id: 'write_file', name: 'Write File', category: 'Filesystem', description: 'Write content to file', params: { path: '', content: '' }, icon: '✏️' },
  { id: 'list_directory', name: 'List Directory', category: 'Filesystem', description: 'Browse folder structure', params: { path: '.' }, icon: '📁' },
  { id: 'delete_file', name: 'Delete File', category: 'Filesystem', description: 'Delete a file', params: { path: '' }, icon: '🗑️' },
  { id: 'fetch_url', name: 'Fetch URL', category: 'Web', description: 'Fetch any URL', params: { url: '' }, icon: '🌐' },
  { id: 'fetch_json', name: 'Fetch JSON', category: 'Web', description: 'GET JSON endpoint', params: { url: '' }, icon: '�' },
  { id: 'search_web', name: 'Search Web', category: 'Web', description: 'Web search query', params: { query: '' }, icon: '🔍' },
  { id: 'stripe_list_products', name: 'List Products', category: 'Stripe', description: 'List Stripe products', params: {}, icon: '📦' },
  { id: 'stripe_create_checkout', name: 'Create Checkout', category: 'Stripe', description: 'New checkout session', params: { price_id: '', customer_email: '' }, icon: '💳' },
  { id: 'stripe_create_tier_checkout', name: 'Tier Checkout', category: 'Stripe', description: 'Tier-based checkout', params: { tier: '', customer_email: '' }, icon: '💎' },
  { id: 'stripe_get_pricing', name: 'Get Pricing', category: 'Stripe', description: 'Get all tier pricing', params: {}, icon: '💰' },
  { id: 'stripe_get_user_tier', name: 'User Tier', category: 'Stripe', description: 'Get user subscription tier', params: { customer_id: '' }, icon: '👤' },
  { id: 'ecosystem_status', name: 'Ecosystem Status', category: 'Ecosystem', description: 'Check service health', params: {}, icon: '💓' },
  { id: 'fulfillment_trigger', name: 'Trigger Fulfillment', category: 'Ecosystem', description: 'Trigger order fulfillment', params: { order_id: '' }, icon: '🚀' },
  { id: 'fulfillment_stats', name: 'Fulfillment Stats', category: 'Ecosystem', description: 'Get fulfillment stats', params: {}, icon: '📊' },
  { id: 'gateway_apikey_create', name: 'Create API Key', category: 'Gateway', description: 'Create new gateway key', params: { name: '', tier: '' }, icon: '🔑' },
  { id: 'gateway_stats', name: 'Gateway Stats', category: 'Gateway', description: 'Get gateway usage', params: {}, icon: '📈' },
  { id: 'aether_list_models', name: 'List Models', category: 'Aether Models', description: 'List available AI models', params: {}, icon: '�' },
  { id: 'aether_chat', name: 'AI Chat', category: 'Aether Models', description: 'Chat with any model', params: { model: 'aether-orion', message: '' }, icon: '💬' },
  { id: 'aether_embed', name: 'Embed Text', category: 'Aether Models', description: 'Generate embeddings', params: { text: '', model: '' }, icon: '🧬' },
  { id: 'aether_generate_image', name: 'Generate Image', category: 'Aether Models', description: 'AI image generation', params: { prompt: '' }, icon: '�' },
  { id: 'browser_navigate', name: 'Browse URL', category: 'Browser', description: 'Open URL in browser', params: { url: '' }, icon: '🌍' },
  { id: 'browser_screenshot', name: 'Screenshot', category: 'Browser', description: 'Take page screenshot', params: {}, icon: '📸' },
  { id: 'browser_click', name: 'Click Element', category: 'Browser', description: 'Click CSS selector', params: { selector: '' }, icon: '🖱️' },
  { id: 'browser_extract', name: 'Extract Text', category: 'Browser', description: 'Extract text by selector', params: { selector: '' }, icon: '📝' },
  { id: 'security_scan_code', name: 'Scan Code', category: 'Security', description: 'Security threat scan', params: { code: '' }, icon: '🛡️' },
  { id: 'security_scan_deps', name: 'Scan Deps', category: 'Security', description: 'Vulnerability scan', params: {}, icon: '🔒' },
];

const SUGGESTIONS = [
  { icon: '💡', text: 'Explain quantum computing in simple terms' },
  { icon: '⚡', text: 'Write a Python function to sort a list' },
  { icon: '�', text: 'Design a landing page for a SaaS startup' },
  { icon: '�', text: 'Analyze the latest Stripe API changelog' },
];

function generateId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

function formatTime(d: Date): string {
  const now = new Date();
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

async function mcpExecute(tool: string, args: Record<string, unknown>) {
  try {
    const res = await fetch(`${MCP_GATEWAY}/mcp/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer nb_prod_atlas_enterprise_a1b2c3d4e5f6g7h8i9j0' },
      body: JSON.stringify({ tool, arguments: args }),
    });
    const data = await res.json();
    return { success: res.ok, data };
  } catch (e) {
    return { success: false, data: { error: String(e) } };
  }
}

function parseToolCalls(text: string): { tool: string; params: Record<string, string> }[] {
  const regex = /```tool_call\s*\n(\{[\s\S]*?\})\n```/g;
  const out: { tool: string; params: Record<string, string> }[] = [];
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text))) {
    try {
      const p = JSON.parse(m[1]);
      if (p.tool) out.push({ tool: p.tool, params: p.params || {} });
    } catch { /* skip */ }
  }
  return out;
}

// === ICONS ===
const Icon = ({ name }: { name: string }) => {
  const m: Record<string, string> = {
    plus: '+', search: '⌕', send: '➤', menu: '☰', trash: '🗑', copy: '⧉', check: '✓',
    chevron: '⌄', tools: '⚙', chat: '�', user: '◉', settings: '⚙', logout: '⏻',
  };
  return <span style={{ fontSize: 16, lineHeight: 1 }}>{m[name] || name}</span>;
};

function TopBar({ model, setModel, onMenu, onTools }: { model: Model; setModel: (m: Model) => void; onMenu: () => void; onTools: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    if (open) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const grouped = useMemo(() => {
    const map = new Map<string, Model[]>();
    MODELS.forEach((m) => {
      const arr = map.get(m.provider) || [];
      arr.push(m);
      map.set(m.provider, arr);
    });
    return Array.from(map.entries());
  }, []);

  return (
    <div className="topbar">
      <div className="topbar-left">
        <button className="icon-btn" onClick={onMenu} title="Toggle sidebar"><Icon name="menu" /></button>
        <div className="model-selector" ref={ref}>
          <button className="model-button" onClick={() => setOpen((o) => !o)}>
            <span className="model-icon" style={{ background: model.iconBg }}>{model.icon}</span>
            <span>{model.name}</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: 4 }}>· {model.context}</span>
            <Icon name="chevron" />
          </button>
          <AnimatePresence>
            {open && (
              <motion.div className="model-dropdown" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}>
                {grouped.map(([provider, models]) => (
                  <div key={provider}>
                    <div className="model-dropdown-header">{PROVIDERS.find((p) => p.id === provider)?.name || provider}</div>
                    {models.map((m) => (
                      <div
                        key={m.id}
                        className={`model-option ${m.id === model.id ? 'active' : ''}`}
                        onClick={() => { setModel(m); setOpen(false); }}
                      >
                        <span className="model-option-icon" style={{ background: m.iconBg, color: 'white' }}>{m.icon}</span>
                        <div className="model-option-info">
                          <div className="model-option-name">{m.name}</div>
                          <div className="model-option-desc">{m.description}</div>
                        </div>
                        {m.tier === 'free' && <span className="model-option-badge" style={{ background: 'rgba(16,163,127,0.15)', color: 'var(--accent-emerald)' }}>FREE</span>}
                        {m.tier === 'enterprise' && <span className="model-option-badge" style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--color-error)' }}>ENT</span>}
                        {m.tier === 'team' && <span className="model-option-badge" style={{ background: 'rgba(139,92,246,0.15)', color: 'var(--accent-purple)' }}>TEAM</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="icon-btn" onClick={onTools} title="Tools"><Icon name="tools" /></button>
        <button className="icon-btn" title="Settings"><Icon name="settings" /></button>
      </div>
    </div>
  );
}

function Sidebar({
  conversations, activeId, setActiveId, newChat, delChat, collapsed, user,
}: {
  conversations: Conversation[];
  activeId: string;
  setActiveId: (id: string) => void;
  newChat: () => void;
  delChat: (id: string) => void;
  collapsed: boolean;
  user: any;
}) {
  if (collapsed) return null;
  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <div className="sidebar-brand-mark">A</div>
          <div>Aether AI</div>
        </div>
        <button className="new-chat-btn" onClick={newChat} style={{ marginTop: 12 }}>
          <Icon name="plus" /> New chat
        </button>
      </div>
      <div className="sidebar-list">
        <div className="sidebar-section-title">Today</div>
        {conversations.map((c) => (
          <div
            key={c.id}
            className={`conv-item ${c.id === activeId ? 'active' : ''}`}
            onClick={() => setActiveId(c.id)}
          >
            <Icon name="chat" />
            <span className="conv-item-title">{c.name}</span>
            {conversations.length > 1 && (
              <button className="conv-item-del" onClick={(e) => { e.stopPropagation(); delChat(c.id); }}>
                <Icon name="trash" />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="sidebar-footer">
        <a className="sidebar-footer-link" href="https://nebulahq.work" target="_blank" rel="noreferrer">🌌 Nebula HQ</a>
        <a className="sidebar-footer-link" href="https://code.nebulahq.work" target="_blank" rel="noreferrer">⚡ Aether Code</a>
        <a className="sidebar-footer-link" href="https://mcp.nebulahq.work" target="_blank" rel="noreferrer">🔌 MCP Gateway</a>
        <div className="user-chip">
          <div className="user-avatar">{(user?.name || user?.email || 'U').slice(0, 1).toUpperCase()}</div>
          <div style={{ flex: 1, fontSize: 13 }}>
            <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{user?.name || 'Guest'}</div>
            <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{user?.tier || 'free'}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}

function ChatArea({ messages, loading, model }: { messages: Message[]; loading: boolean; model: Model }) {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="chat-area-inner">
        <div className="welcome">
          <div className="welcome-title">What can I help with?</div>
          <div className="welcome-sub">Choose a model and start chatting. Tools and code execution are one click away.</div>
          <div className="welcome-grid">
            {SUGGESTIONS.map((s, i) => (
              <button key={i} className="suggestion-card">
                <div className="suggestion-card-icon">{s.icon}</div>
                <div className="suggestion-card-text">{s.text}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-area-inner">
      {messages.map((m) => (
        <div key={m.id} className={`msg ${m.role}`}>
          <div className="msg-avatar">
            {m.role === 'user' ? 'U' : model.icon}
          </div>
          <div className="msg-body">
            <div className="msg-role">
              {m.role === 'user' ? 'You' : model.name}
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 8, fontSize: 11 }}>{formatTime(m.timestamp)}</span>
            </div>
            <div className="msg-content">
              {m.content || (loading && m.role === 'assistant' ? <span className="streaming-dot" /> : '...')}
              {m.toolCalls && m.toolCalls.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  {m.toolCalls.map((tc, i) => (
                    <div key={i} className="tool-call-block">
                      <div className="tool-call-label">
                        <span>{tc.success ? '✓' : '✗'}</span>
                        <span>{tc.tool}</span>
                      </div>
                      <div style={{ color: 'var(--text-tertiary)', marginBottom: 6, fontSize: 12 }}>{JSON.stringify(tc.params, null, 2)}</div>
                      <div style={{ color: 'var(--text-primary)', fontSize: 13, whiteSpace: 'pre-wrap' }}>{tc.result}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}

function Composer({ input, setInput, send, loading, onAttach, attachedTools, setAttachedTools }: {
  input: string; setInput: (s: string) => void; send: () => void; loading: boolean;
  onAttach: () => void; attachedTools: string[]; setAttachedTools: (a: string[]) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.style.height = 'auto';
    if (ref.current) ref.current.style.height = Math.min(ref.current.scrollHeight, 200) + 'px';
  }, [input]);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="composer-wrap">
      <div className="composer">
        <div className="composer-toolbar">
          <button className={`composer-tool ${attachedTools.length > 0 ? 'active' : ''}`} onClick={onAttach}>
            <Icon name="tools" />
            <span>{attachedTools.length > 0 ? `${attachedTools.length} tools` : 'Tools'}</span>
          </button>
          <button className="composer-tool"><span>📎</span><span>Attach</span></button>
          <button className="composer-tool"><span>🎤</span><span>Voice</span></button>
          <div style={{ flex: 1 }} />
          <button className="composer-tool"><span>/</span><span>Commands</span></button>
        </div>
        <textarea
          ref={ref}
          className="composer-input"
          placeholder={`Message ${'Orion'}…`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
        />
        <div className="composer-footer">
          <span className="composer-disclaimer">Aether can make mistakes. Verify important info.</span>
          <button className="send-btn" onClick={send} disabled={!input.trim() || loading}>
            <Icon name="send" />
            <span>{loading ? 'Thinking…' : 'Send'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function ToolsModal({ open, onClose, attachedTools, setAttachedTools, onRunTool }: {
  open: boolean; onClose: () => void;
  attachedTools: string[]; setAttachedTools: (a: string[]) => void;
  onRunTool: (id: string) => void;
}) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const cats = ['All', ...Array.from(new Set(MCP_TOOLS.map((t) => t.category)))];
  const filtered = MCP_TOOLS.filter((t) => (cat === 'All' || t.category === cat) && (q === '' || t.name.toLowerCase().includes(q.toLowerCase()) || t.description.toLowerCase().includes(q.toLowerCase())));
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', width: '90%', maxWidth: 1100, height: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="tools-header" style={{ padding: 20, borderBottom: '1px solid var(--border-subtle)' }}>
          <div className="tools-title">Tools <span style={{ color: 'var(--text-tertiary)', fontSize: 13, fontWeight: 400 }}>({MCP_TOOLS.length} MCP tools, 9 categories)</span></div>
          <input className="tools-search" placeholder="Search tools…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)', padding: '8px 20px', gap: 6, flexWrap: 'wrap' }}>
          {cats.map((c) => (
            <button key={c} className={`composer-tool ${cat === c ? 'active' : ''}`} onClick={() => setCat(c)}>{c}</button>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          <div className="tools-grid">
            {filtered.map((t) => (
              <div key={t.id} className="tool-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="tool-card-icon">{t.icon}</div>
                  <span className="tool-card-cat">{t.category}</span>
                </div>
                <div className="tool-card-name">{t.name}</div>
                <div className="tool-card-desc">{t.description}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                  <button
                    className={`composer-tool ${attachedTools.includes(t.id) ? 'active' : ''}`}
                    onClick={() => setAttachedTools(attachedTools.includes(t.id) ? attachedTools.filter((x) => x !== t.id) : [...attachedTools, t.id])}
                    style={{ flex: 1, justifyContent: 'center' }}
                  >
                    {attachedTools.includes(t.id) ? '✓ Attached' : '+ Attach'}
                  </button>
                  <button className="composer-tool" onClick={() => onRunTool(t.id)} style={{ flex: 1, justifyContent: 'center' }}>Run</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InnerApp() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>(() => {
    try {
      const saved = localStorage.getItem('aether_cons');
      return saved
        ? JSON.parse(saved)
        : [{ id: generateId(), name: 'New chat', messages: [], createdAt: new Date() }];
    } catch {
      return [{ id: generateId(), name: 'New chat', messages: [], createdAt: new Date() }];
    }
  });
  const [activeId, setActiveId] = useState(conversations[0]?.id ?? '');
  const [input, setInput] = useState('');
  const [model, setModel] = useState<Model>(MODELS[0]);
  const [loading, setLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [attachedTools, setAttachedTools] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const active = conversations.find((c) => c.id === activeId);
  const messages = active?.messages ?? [];

  useEffect(() => { localStorage.setItem('aether_cons', JSON.stringify(conversations)); }, [conversations]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const updateMessages = (fn: (ms: Message[]) => Message[]) => {
    setConversations((prev) => prev.map((c) => (c.id === activeId ? { ...c, messages: fn(c.messages) } : c)));
  };

  const newChat = () => {
    const c: Conversation = { id: generateId(), name: 'New chat', messages: [], createdAt: new Date() };
    setConversations((prev) => [c, ...prev]);
    setActiveId(c.id);
  };

  const delChat = (id: string) => {
    if (conversations.length <= 1) return;
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (activeId === id) setActiveId(conversations.find((c) => c.id !== id)?.id ?? '');
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const um: Message = { id: generateId(), role: 'user', content: text, timestamp: new Date() };
    updateMessages((ms) => [...ms, um]);
    setLoading(true);

    try {
      const history = [...messages, um].map((m) => ({ role: m.role, content: m.content }));
      const toolsList = attachedTools.length > 0
        ? MCP_TOOLS.filter((t) => attachedTools.includes(t.id))
        : MCP_TOOLS;
      const systemPrompt = `You are Aether AI, an elite AI assistant powered by ${model.name}. ${model.description}. You have access to MCP tools. To call a tool, output:
\`\`\`tool_call
{"tool": "tool_name", "params": {"key": "value"}}
\`\`\`
Available tools: ${toolsList.map((t) => `${t.name} (${t.id})`).join(', ')}.`;

      const result = await mcpExecute('aether_chat', {
        model: model.id,
        messages: JSON.stringify([{ role: 'system', content: systemPrompt }, ...history]),
      });

      let aiContent = '';
      if (result.success && result.data) {
        const d = result.data as any;
        aiContent = d.response || d.content || d.choices?.[0]?.message?.content || '';
      }
      if (!aiContent) {
        try {
          const res = await fetch(`${MODEL_GATEWAY}/v1/chat/completions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ model: model.id, messages: [{ role: 'system', content: systemPrompt }, ...history], temperature: 0.7 }),
          });
          const data = await res.json();
          aiContent = data.choices?.[0]?.message?.content || '';
        } catch { aiContent = ''; }
      }
      if (!aiContent) aiContent = `[Demo ${model.name}] I received your message: "${text.slice(0, 100)}". In production, this would call ${model.provider}/${model.id} via ${PROVIDERS.find((p) => p.id === model.provider)?.name}.`;

      const toolCalls = parseToolCalls(aiContent);
      const executed: { tool: string; params: Record<string, string>; result: string; success: boolean }[] = [];
      for (const tc of toolCalls) {
        const r = await mcpExecute(tc.tool, tc.params);
        executed.push({ tool: tc.tool, params: tc.params, result: JSON.stringify(r.data).slice(0, 200), success: r.success });
      }

      const am: Message = { id: generateId(), role: 'assistant', content: aiContent, model: model.id, timestamp: new Date(), toolCalls: executed.length ? executed : undefined };
      updateMessages((ms) => [...ms, am]);
    } catch (e) {
      const am: Message = { id: generateId(), role: 'assistant', content: `Error: ${String(e)}`, timestamp: new Date() };
      updateMessages((ms) => [...ms, am]);
    } finally {
      setLoading(false);
    }
  };

  const runTool = async (toolId: string) => {
    const tool = MCP_TOOLS.find((t) => t.id === toolId);
    if (!tool) return;
    const r = await mcpExecute(toolId, tool.params);
    const um: Message = { id: generateId(), role: 'user', content: `Run tool: ${tool.name}`, timestamp: new Date() };
    const am: Message = {
      id: generateId(), role: 'assistant',
      content: r.success ? `**${tool.name}** executed successfully.` : `**${tool.name}** failed.`,
      timestamp: new Date(),
      toolCalls: [{ tool: toolId, params: tool.params, result: JSON.stringify(r.data).slice(0, 500), success: r.success }],
    };
    setConversations((prev) => prev.map((c) => (c.id === activeId ? { ...c, messages: [...c.messages, um, am] } : c)));
  };

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar
        conversations={conversations}
        activeId={activeId}
        setActiveId={setActiveId}
        newChat={newChat}
        delChat={delChat}
        collapsed={sidebarCollapsed}
        user={user}
      />
      <main className="main">
        <TopBar model={model} setModel={setModel} onMenu={() => setSidebarCollapsed((s) => !s)} onTools={() => setToolsOpen(true)} />
        <div className="chat-area">
          <ChatArea messages={messages} loading={loading} model={model} />
        </div>
        <Composer
          input={input}
          setInput={setInput}
          send={send}
          loading={loading}
          onAttach={() => setToolsOpen(true)}
          attachedTools={attachedTools}
          setAttachedTools={setAttachedTools}
        />
      </main>
      <ToolsModal open={toolsOpen} onClose={() => setToolsOpen(false)} attachedTools={attachedTools} setAttachedTools={setAttachedTools} onRunTool={runTool} />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <InnerApp />
    </AuthProvider>
  );
}
